import { Link } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import Seo from "@/components/Seo";
import {
  ArrowRight, PlugsConnected, ShieldCheck, LockKey, UsersThree, Stack,
  ClockCounterClockwise, CloudCheck, Scales, Prohibit, CheckCircle, Envelope,
} from "@phosphor-icons/react";

const GROUPS = [
  {
    tag: "Xero connection",
    title: "Connected with least privilege",
    intro: "Lottie asks Xero for only the permissions she needs to run your month-end — and nothing that lets her touch the rest of your ledger.",
    items: [
      { icon: PlugsConnected, t: "Official Xero OAuth", d: "You authorise the connection inside Xero. Lottie never sees or stores your Xero password, and Xero shows you every permission before you approve it." },
      { icon: ShieldCheck, t: "Reads only what she needs", d: "Invoices and bills, your chart of accounts and trial balance, invoice attachments and the fixed-asset register where used — to build your registers." },
      { icon: Prohibit, t: "Never edits your source data", d: "Lottie only posts manual journals and their reversals. She never edits your invoices, bank transactions or contacts." },
      { icon: PlugsConnected, t: "Disconnect anytime", d: "Revoke access from Xero or disconnect in-app at any moment — the connection is yours to end whenever you like." },
    ],
  },
  {
    tag: "Posting governance",
    title: "Nothing posts without your rules",
    intro: "Every journal that reaches Xero follows the controls you set — with a complete record of who did what.",
    items: [
      { icon: UsersThree, t: "Two-person approval", d: "Turn on four-eyes review and no set-up or release journal posts until a second person approves it." },
      { icon: ClockCounterClockwise, t: "Full audit trail", d: "Every create, amend, post, approval and reversal is logged with before/after detail and exportable activity reports." },
      { icon: CheckCircle, t: "You decide when it posts", d: "Journals can post automatically on schedule or be held for review — the choice is set per practice." },
    ],
  },
  {
    tag: "Access & accounts",
    title: "Strong account security",
    intro: "Sign-in and team access are protected by modern controls out of the box.",
    items: [
      { icon: LockKey, t: "Two-factor authentication", d: "TOTP authenticator-app 2FA is available to everyone and enforced for owner and admin roles; practices can require it for the whole team." },
      { icon: UsersThree, t: "Role-based access", d: "Admins manage the team; read-only reviewers can leave notes but can never edit, post or export financial data." },
      { icon: ShieldCheck, t: "Hardened sign-in", d: "A 12-character password policy, brute-force lockout and an inactivity auto-lock keep sessions safe." },
    ],
  },
  {
    tag: "Data separation",
    title: "Your clients stay isolated",
    intro: "Multi-entity work never means mixed-up data.",
    items: [
      { icon: Stack, t: "Per-entity isolation", d: "Each client organisation is kept separate, with its own chart of accounts and nominal-code classification — no cross-client bleed." },
      { icon: CloudCheck, t: "Reputable cloud infrastructure", d: "Lottie runs on reputable, professionally managed cloud infrastructure." },
      { icon: LockKey, t: "Encrypted in transit", d: "All data moving between your browser, Lottie and Xero travels over encrypted HTTPS (TLS)." },
    ],
  },
];

const RIGHTS = [
  { icon: Scales, t: "UK GDPR & the ICO", d: "WorkWithLottie is registered with the UK's Information Commissioner's Office (ICO), and a Data Processing Agreement (DPA) is available on request." },
  { icon: ClockCounterClockwise, t: "Deletion on your terms", d: "You can request full deletion of your data at any time. Data is removed within 30 days of account closure." },
  { icon: Prohibit, t: "We never sell your data", d: "Your practice and client data is used only to provide the service to you — it is never sold or shared for marketing." },
];

export default function Security() {
  return (
    <MarketingLayout>
      <Seo title="Security & data handling" path="/security"
        description="How WorkWithLottie protects your practice and client data: least-privilege Xero OAuth, two-person approval, a full audit trail, two-factor authentication, per-entity isolation, HTTPS encryption and UK GDPR rights." />

      {/* Hero */}
      <section>
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-10">
          <div className="overline mb-4" style={{ color: "#F37A24" }} data-testid="security-overline">Trust &amp; security</div>
          <h1 className="font-heading font-black text-navy text-4xl sm:text-5xl leading-[1.06] tracking-tighter max-w-3xl" data-testid="security-heading">
            Your clients' data, treated like your own
          </h1>
          <p className="text-navy/70 text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">
            Lottie was built by accountants, for accountants — so security isn't a bolt-on. From a least-privilege Xero
            connection to a full audit trail and two-person approval, every part of the product is designed to keep you
            in control of what happens to your ledger.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 text-sm text-navy/70" data-testid="security-hero-badges">
            {["Least-privilege Xero access", "Full audit trail", "Two-factor authentication", "UK GDPR • ICO registered"].map((b) => (
              <span key={b} className="inline-flex items-center gap-2">
                <CheckCircle size={17} weight="fill" className="text-positive" /> {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Groups */}
      {GROUPS.map((g, gi) => (
        <section key={g.tag} className={gi % 2 === 1 ? "bg-white border-y border-slate-200" : ""} data-testid={`security-group-${g.tag}`}>
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="overline mb-2" style={{ color: "#F37A24" }}>{g.tag}</div>
            <h2 className="font-heading font-bold text-navy text-2xl sm:text-3xl tracking-tight">{g.title}</h2>
            <p className="text-navy/70 text-sm sm:text-base mt-3 max-w-2xl leading-relaxed">{g.intro}</p>
            <div className="grid md:grid-cols-3 gap-5 mt-10">
              {g.items.map((it) => (
                <div key={it.t} className="flex flex-col gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(242,111,33,0.1)" }}>
                    <it.icon size={20} weight="duotone" style={{ color: "#F37A24" }} />
                  </div>
                  <h3 className="font-heading font-bold text-navy">{it.t}</h3>
                  <p className="text-navy/70 text-sm leading-relaxed">{it.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Your data rights */}
      <section className="bg-navy text-white" data-testid="security-rights">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="overline mb-2" style={{ color: "#F37A24" }}>Your data rights</div>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">You own your data</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {RIGHTS.map((r) => (
              <div key={r.t} className="flex flex-col gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/10">
                  <r.icon size={20} weight="duotone" style={{ color: "#F37A24" }} />
                </div>
                <h3 className="font-heading font-bold">{r.t}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <div className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center mb-4" style={{ background: "rgba(242,111,33,0.1)" }}>
          <Envelope size={24} weight="duotone" style={{ color: "#F37A24" }} />
        </div>
        <h2 className="font-heading font-bold text-navy text-2xl sm:text-3xl tracking-tight">Security questions?</h2>
        <p className="text-navy/70 text-sm sm:text-base mt-3 max-w-xl mx-auto leading-relaxed">
          Reviewing Lottie for your practice or need our DPA? We're happy to walk through anything.
        </p>
        <a href="mailto:hello@workwithlottie.com?subject=Security%20enquiry" data-testid="security-contact-email"
          className="mt-6 inline-flex items-center gap-2 text-white font-semibold px-6 py-3.5 rounded-xl" style={{ background: "#F37A24" }}>
          <Envelope size={18} weight="fill" /> hello@workwithlottie.com
        </a>
      </section>

      {/* CTA */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20 text-center">
          <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">Ready to put month-end on autopilot?</h2>
          <div className="flex items-center justify-center gap-2 mt-4 text-navy/70 text-sm">
            <CheckCircle size={17} weight="fill" className="text-positive" /> 7-day free trial · no card required
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/start" data-testid="security-cta-trial" className="text-white font-semibold px-6 py-3.5 rounded-xl flex items-center gap-2" style={{ background: "#F37A24" }}>
              Start free trial <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/faq" data-testid="security-cta-faq" className="bg-white text-navy font-semibold px-6 py-3.5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
              Read the FAQ
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
